import { useId, useState, type ReactNode } from 'react'
import { ArrowSimple } from '@stokr/components-library'
import { Body, BodyClip, BodyInner, Chevron, Header, Root } from './Collapsible.styles'

export interface CollapsibleProps {
  title: ReactNode
  children: ReactNode
  /** Controlled open state. Omit to let the component manage it. */
  open?: boolean
  defaultOpen?: boolean
  onToggle?: (open: boolean) => void
  id?: string
  /** Figma's divided variant: a rule under the title while open. */
  divided?: boolean
  /** Test-automation hook on the title button. */
  dataCy?: string
}

/**
 * One collapsible card: title row with a chevron, body that animates open.
 * Candidate for `@stokr/components-library` once a second app needs it.
 */
export const Collapsible = ({
  title,
  children,
  open,
  defaultOpen = false,
  onToggle,
  id,
  divided = false,
  dataCy,
}: CollapsibleProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const isOpen = open ?? uncontrolledOpen
  const bodyId = useId()

  const toggle = () => {
    if (open === undefined) setUncontrolledOpen(!isOpen)
    onToggle?.(!isOpen)
  }

  return (
    <Root id={id} $open={isOpen}>
      <Header
        type="button"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        data-cy={dataCy}
        onClick={toggle}
        $divided={divided && isOpen}>
        {title}
        <Chevron $open={isOpen} aria-hidden="true">
          <ArrowSimple width={10} height={6} />
        </Chevron>
      </Header>
      <Body id={bodyId} $open={isOpen} inert={!isOpen}>
        <BodyClip>
          <BodyInner $divided={divided}>{children}</BodyInner>
        </BodyClip>
      </Body>
    </Root>
  )
}
